import type { GoodsReceipt } from '@/api/goodsReceipts';
import type { PrintLetterhead } from '@core/api/bookings';
import { formatDocumentDate } from '@core/format/dates';
import { formatMoney } from '@core/format/money';

import { documentClass, type PaperSize } from './paper';

/**
 * A delivery on paper — the goods receipt note (spec §16): the shop, the number and date, who it came
 * from with their challan number, every configuration with its quantity and cost, and the tags each
 * line was given, so the storeman can tick the rail against the sheet. Signed by whoever received and
 * checked it. A cost nobody typed prints an em dash, never a zero.
 */
export function GoodsReceiptSheet({
  paper,
  receipt,
  letterhead,
  showCost,
}: {
  paper: PaperSize;
  receipt: GoodsReceipt;
  letterhead: PrintLetterhead | undefined;
  /** False for whoever may not see what stock cost — the quantities and tags still print. */
  showCost: boolean;
}) {
  const symbol = letterhead?.currencySymbol ?? '₹';
  const money = (value: number | null) => (value === null ? '—' : `${symbol}${formatMoney(value)}`);

  return (
    <article id="printableCard" className={`${documentClass(paper)} px-7 py-6`}>
      <header className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-[1.35em] leading-tight font-bold">
            {letterhead?.tenantName ?? receipt.branchName}
          </p>
          <p className="text-[0.85em]">{letterhead?.branchName ?? receipt.branchName}</p>
          {letterhead?.addressLines.map((line) => (
            <p key={line} className="text-[0.85em] text-[color:var(--doc-muted)]">
              {line}
            </p>
          ))}
          {letterhead?.gstin && <p className="text-[0.85em]">GSTIN {letterhead.gstin}</p>}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[1.05em] font-bold tracking-[0.16em] uppercase">Goods receipt</p>
          <p className="font-mono text-[0.95em] font-semibold">{receipt.documentNumber}</p>
          <p className="text-[0.85em] text-[color:var(--doc-muted)]">
            {formatDocumentDate(receipt.receivedOn)}
          </p>
        </div>
      </header>

      <section className="mt-5 grid grid-cols-2 gap-4 border-t border-[color:var(--doc-rule-strong)] pt-3">
        <div>
          <p className="text-[0.8em] tracking-[0.12em] text-[color:var(--doc-muted)] uppercase">
            Received from
          </p>
          <p className="font-semibold">{receipt.supplierName ?? 'No supplier recorded'}</p>
          {receipt.purchaseOrderNumber && (
            <p className="text-[0.85em]">
              Against order <span className="font-mono">{receipt.purchaseOrderNumber}</span>
            </p>
          )}
        </div>
        <div>
          <p className="text-[0.8em] tracking-[0.12em] text-[color:var(--doc-muted)] uppercase">
            Their challan / invoice
          </p>
          <p className="font-mono">{receipt.supplierReference ?? '—'}</p>
        </div>
      </section>

      <table className="mt-4 w-full border-collapse text-[0.9em]">
        <thead>
          <tr className="border-b border-[color:var(--doc-rule-strong)] text-left text-[0.85em] text-[color:var(--doc-muted)]">
            <th className="py-1.5 pr-2 font-semibold">Design</th>
            <th className="py-1.5 pr-2 text-right font-semibold">Qty</th>
            {showCost && (
              <>
                <th className="py-1.5 pr-2 text-right font-semibold">Cost each</th>
                <th className="py-1.5 text-right font-semibold">Amount</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {receipt.lines.map((line) => (
            <tr key={line.id} className="border-b border-[color:var(--doc-rule)] align-top">
              <td className="py-1.5 pr-2">
                <p className="font-semibold">{line.itemName}</p>
                <p className="text-[0.85em] text-[color:var(--doc-muted)]">
                  {line.variantDisplayName}
                </p>
                <p className="font-mono text-[0.8em]">{line.serialCodes.join(', ')}</p>
              </td>
              <td className="py-1.5 pr-2 text-right">{line.quantity}</td>
              {showCost && (
                <>
                  <td className="py-1.5 pr-2 text-right">{money(line.unitCost)}</td>
                  <td className="py-1.5 text-right">
                    {money(line.unitCost === null ? null : line.unitCost * line.quantity)}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex items-baseline justify-between border-y border-[color:var(--doc-rule-strong)] py-2">
        <span className="font-bold">
          {receipt.totalUnits} garment{receipt.totalUnits === 1 ? '' : 's'}
        </span>
        {showCost && <span className="text-[1.15em] font-bold">{money(receipt.totalCost)}</span>}
      </div>

      {receipt.notes && <p className="mt-3 text-[0.9em]">{receipt.notes}</p>}

      <footer className="mt-10 flex items-end justify-between gap-6 text-[0.8em] text-[color:var(--doc-muted)]">
        <span className="border-t border-[color:var(--doc-rule)] pt-1">Received by</span>
        <span className="border-t border-[color:var(--doc-rule)] pt-1">Checked by</span>
      </footer>
    </article>
  );
}
